import React from 'react';
import PropTypes from 'prop-types';
import css from './CwTextField.scss';
import cx from 'classnames';
import { AlignmentEnum } from '../../utils/Enums';
import changeCheck from '../../utils/ChangeCheck';

class CwTextField extends React.Component {
  shouldComponentUpdate(nextProps) {
    const propsToCheck = ['value', 'disabled', 'invalid'];
    return changeCheck(propsToCheck, this.props, nextProps);
  }
  /**
   * This method fires off following a key press for the input field.
   * If the field is enabled it will check for a blank value first and accept it
   * thus accepting back space and then check to see if we have a regex. If we do
   * we need to check if the regex we have matches the value we've been supplied from
   * the event. If so pass it on otherwise, return
   * @param e
   */
  handleChange = (e) => {
    if (!this.props.onChange) {
      return;
    }

    const value = e.target.value;

    if (value === '') {
      this.props.onChange(value);
      return;
    }
    if (this.props.regex) {
      if (!this.matchRegex(this.props.regex, value)) {
        return;
      }
    }
    this.props.onChange(value);
  };

  /**
   * This method will take the regex String passed from it's parent, convert it to a regex object
   * and call test against the second parameter. Will retrun true if val passes the regex check.
   * @param reg - The string we want to conver to regex
   * @param val - the string we want to compare against
   * @returns {boolean}
   */
  matchRegex = (reg, val) => {
    const regex = new RegExp(reg, "gm");
    if (regex.test(val)) {
      return true;
    }
    return false;
  };

  focus = () => {
    this.inputField.focus();
  };

  render() {
    return (
      <input
        data-cwid={this.props.cwId}
        data-cwdata={"focusable"}
        className={cx(this.props.invalid ? css.textFieldInvalid : css.textField, this.props.className, {
          [css.alignCenter]: this.props.textAlignment ? this.props.textAlignment === AlignmentEnum.CENTER : false,
          [css.alignRight]: this.props.textAlignment ? this.props.textAlignment === AlignmentEnum.RIGHT : false,
        })}
        placeholder={this.props.placeholder}
        readOnly={this.props.readOnly}
        //Set value to blank string if value is undefined/null, this removes the controlled/uncontrolled error in the console. 
        value={this.props.value ? this.props.value : ''}
        onChange={this.handleChange}
        onKeyPress={this.props.onKeyPress}
        onFocus={this.props.onFocus}
        onBlur={this.props.onBlur}
        onKeyDown={this.props.onKeyDown}
        onClick={this.props.onClick}
        onKeyUp={this.props.onKeyUp}
        tabIndex={this.props.disabled ? -1 : this.props.tabIndex}
        disabled={this.props.disabled}
        id={this.props.id}
        type={this.props.type}
        maxLength={this.props.maxLength}
        ref={(input) => this.inputField = input}
        autoFocus={this.props.autoFocus}
        style={this.props.style}
      />
    );
  }
}

CwTextField.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Text in textfield
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * Hint text, displayed when user has not typed anything in
   */
  placeholder: PropTypes.string,
  /**
   * Used to set textfield to a read only mode
   */
  readOnly: PropTypes.bool,
  /**
   * Callback function that is fired when textfield's text changes
   */
  onChange: PropTypes.func,
  /**
   * Callback function that is fired when any key is pressed
   */
  onKeyPress: PropTypes.func,
  /**
   * Used to control order when tabbing through items
   */
  tabIndex: PropTypes.number,
  /**
   * Used to control if text is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable textfield
   */
  disabled: PropTypes.bool,
  /**
   * Used as an identifier for the component
   */
  id: PropTypes.string,
  /**
   * Used to limit the inputs of the field by matching against provided string.
   */
  regex: PropTypes.string,
  /**
   * Callback function that is fired when textfield is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when textfield is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Callback function that is fired on key down
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback function that is fired on mouse click
   */
  onClick: PropTypes.func,
  /**
   * Callback function that is fired on key up
   */
  onKeyUp: PropTypes.func,
  /**
   * Change the input field type
   */
  type: PropTypes.string,
  /**
   * The maximium number of characters allowed in the field.
   */
  maxLength: PropTypes.number,
  /**
   * Used to control if textfield is autofocused when the page loads
   */
  autoFocus: PropTypes.bool,
  /**
   * An override for text alignments. Checks 'CENTER' and 'RIGHT' as arguments.
   * Will default to left otherwise or if left blank.
   */
  textAlignment: PropTypes.oneOf([AlignmentEnum.CENTER, AlignmentEnum.RIGHT]),
  /**
   * Override the styles of the root element
   */
  style: PropTypes.object,
  /**
   * The css class name to be appended to default classname
   */
  className: PropTypes.string,
};

CwTextField.defaultProps = {
  tabIndex: 0,
};

export default CwTextField;


// WEBPACK FOOTER //
// ./src/components/cwtextfield/CwTextField.jsx